import { ReactNode, ChangeEvent, useState, useCallback, memo } from 'react'

import { AccordionProps } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Variant } from '@material-ui/core/styles/createTypography'

import { IntroductionStyleComponent } from './IntroductionStyleComponent'

interface OwnProps {
  title: ReactNode
  onChange?: (isExpanded: boolean) => void
  isClosable?: boolean
  onClose?: () => void
  children?: AccordionProps['children']
  headline?: string
  info?: string
  stylesVariant: IntroductionStylesVariant
}

type IntroductionProps = Omit<
  AccordionProps,
  'onChange' | 'title' | 'children'
> &
  OwnProps

export enum IntroductionStylesVariant {
  GreyBackground,
  SlimVariant,
  LightBackgroundVariant
}

const Introduction = ({
  title,
  children,
  headline,
  info,
  expanded = false,
  onChange,
  isClosable = false,
  onClose,
  stylesVariant
}: IntroductionProps) => {
  const [isExpanded, setIsExpanded] = useState(expanded)
  const [isClosed, setIsClosed] = useState(false)
  const theme = useTheme()

  const handleChange = useCallback(
    (_: ChangeEvent<unknown>, didExpand: boolean) => {
      setIsExpanded(didExpand)

      if (onChange) {
        onChange(didExpand)
      }
    },
    [setIsExpanded, onChange]
  )

  const handleClose = useCallback(() => {
    if (!isClosable) {
      return
    }

    if (onClose) {
      onClose()
    }

    setIsClosed(true)
  }, [isClosable, onClose])

  if (isClosed) {
    return null
  }

  const introductionStyles = {
    [IntroductionStylesVariant.LightBackgroundVariant]: {
      accordionBackgroundColor: theme.palette.primary.light,
      withBoxShadow: true
    },
    [IntroductionStylesVariant.GreyBackground]: {
      accordionBackgroundColor: theme.palette.grey[50],
      titleVariant: 'h5' as Variant,
      detailsTitleVariant: 'caption' as Variant,
      lineHeight: '1.75rem',
      withBoxShadow: true
    },
    [IntroductionStylesVariant.SlimVariant]: {
      accordionBackgroundColor: theme.palette.common.white,
      titleFontSize: '0.875rem',
      titleFontSizeDesktop: '1rem',
      withBoldTitle: true,
      titleColor: 'primary' as const,
      withSlimExpandIcon: true,
      withNoPadding: true
    }
  }

  const getIntroductionContent = () => {
    if (info) {
      if (headline) {
        return `### ${headline}\n\n${info}`
      }

      return info
    }

    if (children) {
      return children
    }

    return ''
  }

  return (
    <IntroductionStyleComponent
      title={title}
      isClosable={isClosable}
      handleChange={handleChange}
      handleClose={handleClose}
      isExpanded={isExpanded}
      {...introductionStyles[stylesVariant]}
    >
      {getIntroductionContent()}
    </IntroductionStyleComponent>
  )
}

export default memo(Introduction)
